import { BehaviorSubject, combineLatest, Subject, Subscription, timer } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { EventBusService } from 'src/app/services/event-bus.service';
import { OnDestroy$ } from '@pdtec/ngx-observable-lifecycle';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HotelToursFiltersComponent } from './filters/filters.component';
import { HotelTourComponent } from './hotel-tour/hotel-tour.component';
import { HttpHotelToursSearchService } from '../../services/search/hotel-tours-requests.service';
import { HotelToursSearchService } from '../../services/search/hotel-tours-search.service';
import { HotelToursResultTour } from '../../types/interfaces/hotel-tour-search';
import { SearchProgressService } from '../../services/search-progress.service';
import { HotelTourSkeletonComponent } from './hotel-tour/hotel-tour-skeleton/hotel-tour-skeleton.component';
import { ToursNotFoundComponent } from '../tours/tours-not-found/tours-not-found.component';
import { WindowRefService } from '../../services/window-ref.service';
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../config/hotel-tours-list-page.config';
import { SearchProgressLinearComponent } from '../search-progress-linear/search-progress-linear.component';
import { pluralize } from '../pipes/pluralize/pluralize';

@Component({
    selector: 'app-hotel-tours',
    templateUrl: environment.mobile ? './hotel-tours.component.mobile.html' : './hotel-tours.component.html',
    styleUrls: environment.mobile ? ['./hotel-tours.component.mobile.scss'] : ['./hotel-tours.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        HotelTourComponent,
        HotelTourSkeletonComponent,
        ToursNotFoundComponent,
        HotelToursFiltersComponent,
        SearchProgressLinearComponent,
    ]
})
export class HotelToursComponent extends OnDestroy$ implements OnInit, OnDestroy {
    private scrollAnimationFrameId: number | null = null;
    private mainSearchSubscription: Subscription;
    private completeSearch$: Subject<any>;
    private destroy$ = new Subject<void>();

    searchId: string;
    tours: HotelToursResultTour[];
    isFindTours: boolean;
    searchIsDone: boolean;
    trackByFn: any;
    isShowCallback = false;
    isSearchProgressActive = false;
    showLimit = 6;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private httpSearchService: HttpHotelToursSearchService,
        private searchService: HotelToursSearchService,
        private eventBus: EventBusService,
        private cdRef: ChangeDetectorRef,
        @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
        private searchProgressService: SearchProgressService,
        private window: WindowRefService,
    ) {
        super();
    }

    ngOnDestroy() {
        if (this.scrollAnimationFrameId !== null) {
            this.window.nativeWindow.cancelAnimationFrame(this.scrollAnimationFrameId);
            this.scrollAnimationFrameId = null;
        }
        this.destroy$.next(); // Сообщаем об уничтожении
        this.destroy$.complete(); // Завершаем поток
        super.ngOnDestroy(); // Вызываем деструктор родителя
    }

    ngOnInit() {
        this.searchService.searchStartFailed$
            .pipe(takeUntil(this.destroy$))
            .subscribe(failed => {
                this.completeSearch$?.next(undefined);
                this.completeSearch$?.complete();
                this.mainSearchSubscription?.unsubscribe();
                this.tours = [];
                this.isFindTours = false;
                this.searchIsDone = failed;
                this.searchProgressService.end();
                this.isSearchProgressActive = false;
                this.cdRef.detectChanges();
            });

        this.searchService.newSearchStarted$
            .pipe(
                filter(searchId => !!searchId),
                takeUntil(this.destroy$)
            )
            .subscribe((searchId) => {
                this.scrollToResultsIfNeeded();
                this.completeSearch$?.next(undefined);
                this.completeSearch$?.complete();
                const analyticsContext = this.searchService.getAnalyticsContext();
                const requestVersion = this.searchService.getRequestVersion();
                const searchSource = this.searchService.getLastSearchSource();

                this.searchId = searchId;
                this.tours = [];
                this.isFindTours = false;
                this.searchIsDone = false;
                this.showLimit = 6;
                this.cdRef.detectChanges();

                this.completeSearch$ = new Subject<void>();
                const timerSrc = new BehaviorSubject<number>(0);
                const EMPTY_TOURS_POLL_INTERVAL = 3000;
                const FOUND_TOURS_POLL_INTERVAL = 5000;
                this.searchProgressService.start();
                this.isSearchProgressActive = true;

                const handleError = (error: any) => {
                    if (!this.searchService.isCurrentRequest(requestVersion)) { return; }
                    this.searchService.trackSearchError(error, analyticsContext);
                    this.eventBus.hideToursLoader();
                    this.searchProgressService.end();
                    this.isSearchProgressActive = false;
                    this.searchIsDone = true;
                    this.searchService.searchIsDone(this.searchId);
                    this.completeSearch$.next(undefined);
                    this.completeSearch$.complete();
                    this.cdRef.detectChanges();
                };

                let isLoadFilters = false;
                let hasTours = false;
                this.showCallback(false);

                this.mainSearchSubscription = timerSrc.asObservable()
                    .pipe(
                        takeUntil(this.completeSearch$),
                        switchMap((time: number) => timer(time)),
                        switchMap(() =>
                            combineLatest([
                                this.httpSearchService.getToursByHotelId(this.searchId),
                            ]).pipe(
                                takeUntil(this.completeSearch$),
                                map(([toursResponse]) => toursResponse),
                            )
                        )
                    )
                    .subscribe({
                        next: (toursResponse) => {
                            if (!this.searchService.isCurrentRequest(requestVersion)) { return; }
                            if (toursResponse.error) {
                                handleError(toursResponse.error);
                                return;
                            }
                            const hasVariants = Array.isArray(toursResponse.tours) && toursResponse.tours.length > 0;

                            if (hasVariants) {
                                setTimeout(() => {
                                    if (!this.searchService.isCurrentRequest(requestVersion)) { return; }
                                    this.isFindTours = true;
                                    this.tours = [...toursResponse.tours];
                                    this.cdRef.detectChanges();
                                }, 300);

                                if (!isLoadFilters && searchSource !== 'filters') {
                                    this.httpSearchService.isFindFirstToursSub.next(searchId);
                                    isLoadFilters = true;
                                }

                                if (!hasTours) {
                                    this.showCallback(true);
                                    hasTours = true;
                                    this.searchService.firstToursFound();
                                }

                            }

                            if (toursResponse.searchIsDone === true) {
                                this.searchService.trackSearchResult(hasTours ? 'search_result_success' : 'search_result_empty', analyticsContext);
                                setTimeout(() => {
                                    if (!this.searchService.isCurrentRequest(requestVersion)) { return; }
                                    this.searchProgressService.end();
                                    this.isSearchProgressActive = false;
                                    this.searchService.searchIsDone(this.searchId);
                                    this.completeSearch$.complete();
                                    this.searchIsDone = true;
                                    this.cdRef.detectChanges();

                                }, 300);

                                if (hasTours && hasVariants && searchSource !== 'filters') {
                                    this.searchService.isLoadFilters(this.searchId);
                                }

                                if (!hasTours) {
                                    this.searchService.notFoundTours();

                                }

                            } else {
                                const nextInterval = hasTours
                                    ? FOUND_TOURS_POLL_INTERVAL
                                    : EMPTY_TOURS_POLL_INTERVAL;
                                timerSrc.next(nextInterval);
                            }

                            this.showCallback(true);
                        },
                        error: handleError
                    });
            });
    }

    writeOnWhatsApp() {
        const btn = document.querySelector('.btn-write-on');
        if (btn) {
            const event = new MouseEvent('click', {bubbles: true, cancelable: true});
            btn.dispatchEvent(event);
        }

        this.window.nativeWindow.brxTracking.sendAnalyticsEvent('write-to-whatsapp-click', {
            Place: 'listing',
            Page: 'tours-listing'
        });
    }

    showCallback(value): boolean {
        let enable = this.window.nativeWindow?.app?.isCallbackEnabled;
        if (!enable) return false;

        this.isShowCallback = value;
    }

    get visibleTours(): HotelToursResultTour[] {
        if (!this.tours) {
            return [];
        }
        return this.tours.slice(0, this.showLimit);
    }

    get remainingToursCount(): number {
        if (!this.tours || this.tours.length <= this.showLimit) {
            return 0;
        }
        return this.tours.length - this.showLimit;
    }

    get nextToursBatchCount(): number {
        return Math.min(10, this.remainingToursCount);
    }

    get showMoreLabel(): string {
      const nextCount = this.nextToursBatchCount;
        return `Показать еще ${nextCount} ${pluralize(nextCount, this.isHotelsSearch ? ['вариант', 'варианта', 'вариантов'] : ['тур', 'тура', 'туров'])}`;
    }

    get shouldShowFilters(): boolean {
        if (this.isFindTours || !this.searchIsDone) {
            return true;
        }

        return this.searchService.getLastSearchSource() === 'filters';
    }

    showAll() {
        this.showLimit += this.nextToursBatchCount;
        this.cdRef.detectChanges();
    }

    private scrollToResultsIfNeeded() {
        if (environment.mobile) {
            return;
        }

        const source = this.searchService.getLastSearchSource();
        if (source !== 'form' && source !== 'calendar') {
            return;
        }

        const wrapper = document.querySelector('.ng-hotel-tours-wrapper') as HTMLElement | null;
        const targetElement = wrapper ?? this.elementRef.nativeElement;
        const top = targetElement.getBoundingClientRect().top + this.window.nativeWindow.scrollY - 16;
        this.animateScrollTo(Math.max(0, top));
    }

    private animateScrollTo(targetTop: number) {
        if (this.scrollAnimationFrameId !== null) {
            this.window.nativeWindow.cancelAnimationFrame(this.scrollAnimationFrameId);
            this.scrollAnimationFrameId = null;
        }

        const startTop = this.window.nativeWindow.scrollY;
        const distance = targetTop - startTop;

        if (Math.abs(distance) < 4) {
            this.window.nativeWindow.scrollTo(0, targetTop);
            return;
        }

        const duration = 650;
        const startTime = this.window.nativeWindow.performance.now();

        const easeInOutCubic = (progress: number) => {
            return progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        };

        const step = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            this.window.nativeWindow.scrollTo(0, startTop + distance * easedProgress);

            if (progress < 1) {
                this.scrollAnimationFrameId = this.window.nativeWindow.requestAnimationFrame(step);
                return;
            }

            this.scrollAnimationFrameId = null;
        };

        this.scrollAnimationFrameId = this.window.nativeWindow.requestAnimationFrame(step);
    }

    get isHotelsSearch(): boolean {
        return !environment.mobile && !!this.searchService.getLastSearchParams()?.hotelsSearch;
    }
}
